import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../../components/layout/view/ViewHeader";
import EmptyState from "../../../../../components/feedback/EmptyState";
import ContentGrid from "../../../../../components/grids/content/ContentGrid";
import AlbumThumbnailCard from "../../../../../components/cards/thumbnails/albums/AlbumThumbnailCard";

import { useGroupAlbums } from "../hooks/useGroupAlbums";

import "./GroupMemberAlbumsView.css";

const DEFAULT_LIMIT = 12;
const PREVIEW_LIMIT = 5;

const getErrorMessage = (error) =>
  typeof error === "string"
    ? error
    : error?.message || "Unable to load group albums.";

export default function GroupMemberAlbumsView({
  groupTag,
  limit = DEFAULT_LIMIT,
  preview = false,
  onContentLoaded,
  onPreviewResult,
}) {
  const navigate = useNavigate();

  const pageLimit = preview
    ? PREVIEW_LIMIT
    : Math.max(1, Number(limit) || DEFAULT_LIMIT);

  const {
    albums,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMoreAlbums,
    refreshAlbums,
  } = useGroupAlbums({
    groupTag,
    limit: pageLimit,
  });

  const albumList = Array.isArray(albums) ? albums : [];

  const visibleAlbums = preview ? albumList.slice(0, PREVIEW_LIMIT) : albumList;

  const errorMessage = error ? getErrorMessage(error) : "";
  const isEmpty = !loading && visibleAlbums.length === 0;

  useEffect(() => {
    if (!preview || loading) return;

    const notify = onContentLoaded || onPreviewResult;

    notify?.({
      hasContent: visibleAlbums.length > 0,
      error: Boolean(errorMessage),
    });
  }, [
    preview,
    loading,
    visibleAlbums.length,
    errorMessage,
    onContentLoaded,
    onPreviewResult,
  ]);

  const handleOpenAlbum = (album) => {
    const albumTag = typeof album === "string" ? album : album?.albumTag;

    if (!albumTag) return;

    navigate(`/albums/${encodeURIComponent(albumTag)}`);
  };

  const handleLoadMore = async () => {
    if (preview || loading || loadingMore || !hasMore) return;

    await loadMoreAlbums();
  };

  if (preview && isEmpty && !errorMessage) {
    return null;
  }

  return (
    <ViewContainer>
      <ViewHeader
        title={`Group Albums (${visibleAlbums.length})`}
        description="Albums shared with this group."
      />

      {errorMessage && visibleAlbums.length > 0 && (
        <div className="group-albums__alert" role="alert">
          {errorMessage}
        </div>
      )}

      <div>
        {isEmpty ? (
          !preview && (
            <EmptyState
              title={
                errorMessage ? "Unable to load albums" : "No albums available"
              }
              description={
                errorMessage ||
                "Albums shared with this group will appear here."
              }
            />
          )
        ) : (
          <ContentGrid
            items={visibleAlbums}
            loading={loading && !visibleAlbums.length}
            columns={4}
            tabletColumns={3}
            mobileColumns={2}
            gap={16}
            showEmpty={false}
            skeletonCount={preview ? PREVIEW_LIMIT : 8}
            renderItem={(album) => (
              <AlbumThumbnailCard
                key={album.albumId ?? album.albumTag}
                album={album}
                onClick={handleOpenAlbum}
              />
            )}
          />
        )}
      </div>

      {!preview && hasMore && visibleAlbums.length > 0 && (
        <div className="group-albums__footer">
          <button
            type="button"
            className="group-albums__load-more"
            disabled={loading || loadingMore}
            onClick={handleLoadMore}
          >
            {loadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </ViewContainer>
  );
}
